import get from "lodash/get";
import set from "lodash/set";
import find from "lodash/find";
import pick from "lodash/pick";
import size from "lodash/size";
import omit from "lodash/omit";
import sortBy from "lodash/sortBy";
import isEmpty from "lodash/isEmpty";
import includes from "lodash/includes";
import capitalize from "lodash/capitalize";

import moment from "moment";
import { validURL } from "../utils/string-helpers";
import { DATE_FORMATS, defaultFirebaseProjectId } from "../constants";
import { setSession } from "../utils/session-helper";

const { DATE_DAY_FORMAT } = DATE_FORMATS;

async function quickEditRemoveSetRequests(
  { editExercise, editExercise: { sets } },
  api,
  proxyAthlete,
  reqSource,
  flash,
  index,
  organizationCoach = undefined
) {
  try {
    // If there is only one set left and user removes it, then delete exercise.
    let queryRequest = `/${editExercise._id}/?proxyAthlete=${proxyAthlete}&reqSource=${reqSource}
    /${editExercise._id}/?proxyAthlete=${proxyAthlete}&reqSource=${reqSource}`;

    if (organizationCoach) {
      queryRequest = queryRequest + `&organizationCoach=${organizationCoach}`;
    }

    if (sets.length === 1) {
      await api(`exercise` + queryRequest, {
        method: "DELETE",
        body: editExercise,
      });
      flash("success", "Successfully removed");
    }
    // Otherwise, simply remove the single set.
    else {
      sets.splice(index, 1);
      await api(`exercise` + queryRequest, {
        method: "PUT",
        body: editExercise,
      });

      flash("success", "Successfully removed");
    }
  } catch (err) {
    console.log("error:", err);
  }
}

function validationsForNewSet(newSet, defaultRepMax, isAssistant, assert) {
  assert(newSet.sets > 0, 400, "Sets must be greater than 0.");

  if (newSet.rpe !== "AMRAP") {
    assert(newSet.reps > 0, 400, "Reps must be greater than 0.");
  }

  assert(newSet.sets <= 500, 400, "An exercise cannot have that many sets.");

  assert(
    isAssistant || defaultRepMax > 0,
    400,
    "Invalid Rep Maximum, try editing the value."
  );
}

async function copySecondaryMovement(body, user, api) {
  if (!body || !body.primary || !body.movement) return;
  let library = get(user, `exerciseLibrary.${body.primary}`, []);
  const exists = find(library, { movement: body.movement });
  if (exists) return;

  const primary = body.primary;
  delete body.primary;

  library.push(body);
  library = sortBy(library, [
    (secondary) => String(secondary.movement).toLowerCase(),
  ]);

  set(user, `exerciseLibrary.${primary}`, library);

  await api(`user/${user._id}`, {
    method: "PUT",
    body: pick(user, "exerciseLibrary"),
  });
}

const Exercise = (app) => {
  app
    // Quick Update
    .post(
      "/exercise/quick-edit",
      async ({ req, res, assert, flash, locals, api, session, load }) => {
        const {
          body: {
            exercises,
            activeUserToRequest,
            organizationCoach = undefined,
          },
          query = {},
        } = req;

        const { proxyAthlete, reqSource } = query;

        await load("profileUser", { user: proxyAthlete });

        let organizationQuery = ``;

        if (organizationCoach) {
          organizationQuery = `&organizationCoach=${organizationCoach}`;
        }

        const { profileUser } = locals;
        if (exercises.length) {
          await Promise.all(
            exercises.map(async (exercise) => {
              if (exercise.movementLibrary) {
                await copySecondaryMovement(
                  exercise.movementLibrary,
                  profileUser,
                  api
                );
              }
              return api(
                `exercise/${exercise._id}?proxyAthlete=${proxyAthlete}&reqSource=${reqSource}` +
                  organizationQuery,
                { method: "PUT", body: exercise }
              );
            })
          );

          flash("success", "Successfully saved");
        }
        res.refreshWith({ edit: "" }, { url: "back" });
      }
    )
    .post(
      "/exercise/quick-edit-note",
      async ({ req, res, flash, api, load }) => {
        const { body } = req;

        await api(`workout-notes`, { method: "POST", body });
        flash("success", "Successfully saved");

        res.backDontScroll();
      }
    )
    .post(
      "/exercise/quick-edit-coach-note",
      async ({ req, res, flash, api, load }) => {
        const { body } = req;

        await api(`coach-notes`, { method: "POST", body });
        flash("success", "Successfully saved");

        res.backDontScroll();
      }
    )
    // Create or update an exercise
    .post(
      "/exercise/save/:exercise?",
      async ({ req, res, assert, flash, locals, api, load, session }) => {
        const { body, params, query = {} } = req;
        const { proxyAthlete, reqSource } = query;

        const organizationCoach = body.organizationCoach || undefined;

        // proxyAthlete and profileUser if action is by coach.
        await load("editExercise", {
          exercise: params.exercise,
          proxyAthlete: query.proxyAthlete,
          organizationCoach: organizationCoach,
        });
        await load("profileUser", { user: query.proxyAthlete });

        const { editExercise, profileUser } = locals;

        const activeUser = profileUser || locals.activeUserToRequest;
        const _id = get(editExercise, "_id");

        const isNew = !_id;

        const isAssistant =
          get(editExercise, "type", body.type) === "assistant";

        if (isAssistant) {
          body.sets = body.sets.map((set) => {
            // clean up if equipped when it was initally set then type changed
            if (set.equipment) delete set.equipment;
            if (set.equipped) delete set.equipped;
            if (set.equippedRepMax) delete set.equippedRepMax;

            if (set.load || set.rpe) return set;
            set.load = 0;
            return set;
          });
        }

        body.user = activeUser._id;

        if (!body.cycle)
          assert(body.date, 400, "You must specify a day for the workout.");
        assert(body.primary, 400, "A primary exercise is required.");
        if (includes(["secondary", "assistant"], body.type))
          assert(body.secondary, 400, `You must specify ${body.type} exercise`);

        assert(
          size(body.sets),
          400,
          "You must add at least one set to the exercise."
        );

        _(body)
          .set("sets", body.sets || null)
          .set("notes", body.notes || null)
          .set("secondary", body.secondary || null)
          .set("parameters", body.parameters || null)
          .set("video", body.video || null)
          .set("instructions", body.instructions || null)
          .set("exerciseTypeField", body.exerciseTypeField || null)
          .set("primaryMuscleGroup", body.primaryMuscleGroup || null)
          .set("secondaryMuscleGroup", body.secondaryMuscleGroup || null)
          .defaultsDeep(
            { repMaximums: editExercise.repMaximums },
            { repMaximums: activeUser.repMaximums }
          )
          .value();

        assert(
          isAssistant || !isEmpty(body.repMaximums),
          400,
          "You must specify a rep maximum for the exercise."
        );
        await copySecondaryMovement(body.movementLibrary, profileUser, api);
        delete body.movementLibrary;
        delete body.organizationCoach;

        body.sets.forEach((set) => {
          if (typeof set.reps === "string" && set.reps.includes("-")) {
            const [repsLower, reps] = set.reps.split("-");
            set.reps = reps;
            set.repsLower = repsLower;
          }
        });

        let exerciseQuery = `/?proxyAthlete=${query.proxyAthlete}&reqSource=${reqSource}`;

        if (organizationCoach) {
          exerciseQuery =
            exerciseQuery + `&organizationCoach=${organizationCoach}`;
        }

        if (isNew) {
          await api(`exercise` + exerciseQuery, { method: "POST", body });
          flash("success", "Successfully added");
        } else {
          await api(`exercise/${_id}` + exerciseQuery, { method: "PUT", body });
          flash("success", "Successfully updated");
        }

        res.refreshWith(
          {
            modal: "",
            editMode: "",
            movement: "",
            scrollTo: _id,
            secondaryAdded: "",
          },
          { url: "back", hash: "#_" }
        );
      }
    )
    // Remove an exercise
    .get(
      "/exercise/remove/:exercise",
      async ({ req, res, assert, flash, locals, api, load, session }) => {
        if (
          process.browser &&
          !confirm("Are you sure you want to delete this exercise?")
        ) {
          return res.backDontScroll();
        }

        const {
          params,
          query: { proxyAthlete },
        } = req;

        let editExerciseQuery = { exercise: params.exercise, proxyAthlete };
        if (locals.activeUser.organizationId) {
          editExerciseQuery = {
            ...editExerciseQuery,
            organizationCoach: locals.activeUserToRequest._id,
          };
        }
        await load("editExercise", editExerciseQuery);
        const { editExercise, activeUserToRequest } = locals;

        assert(editExercise, 400, "Exercise has already been removed");

        await api(
          `exercise/${editExercise._id}?proxyAthlete=${proxyAthlete}&organizationCoach=${activeUserToRequest._id}`,
          {
            method: "DELETE",
          }
        );

        flash("success", "Successfully removed");
        res.backDontScroll();
      }
    )
    // Unschedule a workout: Remove an entire workout (list of exercises).
    .post(
      "/exercise/unschedule-workout",
      async ({ req, res, assert, flash, api, locals, load }) => {
        const { body, query = {} } = req;
        const { silent, source = "" } = body;
        const { proxyAthlete } = query;

        if (proxyAthlete) {
          locals.athlete = get(locals.activeUser, "athletes", []).find(
            (a) => String(a._id) === proxyAthlete
          );
        }
        delete body.silent;
        delete body.source;
        assert(body.days, 400, "Select workout to remove from the calendar.");
        // trying the remove all logic
        const startingCalendarDay = locals.activeUser.startingCalendarDay || 0;
        moment.locale("en", {
          week: {
            dow: startingCalendarDay,
          },
        });
        let period = moment(body.days[0], DATE_DAY_FORMAT),
          start,
          end;
        switch (source) {
          case "calendarDayView":
            start = period.clone().add(-1, "days");
            end = period.clone().add(1, "days");
            break;

          case "calendarWeekView":
            start = period.clone().startOf("week");
            end = period.clone().endOf("week");
            break;

          default:
            const sortedDays = [...body.days].sort();
            const monthStart = moment(sortedDays[0], DATE_DAY_FORMAT);
            const monthEnd = moment(sortedDays.at(-1), DATE_DAY_FORMAT);
            start = monthStart.clone().startOf("month").startOf("week");
            end = monthEnd.clone().endOf("month").endOf("week");
        }
        await load("activeExercises", { start, end, proxyAthlete });
        const exercises = locals.activeExercises
          .filter((ex) => {
            return body.days.includes(
              moment(ex.utcDate.toString()).format(DATE_DAY_FORMAT)
            );
          })
          .map((exercise) => exercise._id);
        await api(`exercise-remove-multiple?proxyAthlete=${proxyAthlete}`, {
          method: "POST",
          body: {
            exercises,
          },
        });
        moment.locale("en", {
          week: {
            dow: 0,
          },
        });
        flash(
          "success",
          `Workout successfully unscheduled.${
            silent ? " Click Add to proceed." : ""
          }`
        );

        res.backDontScroll();
        // END of remove all logic
      }
    )
    // Copy one day of exercises to another.
    .post(
      "/exercise/copy-day",
      async ({ req, res, assert, flash, api, session }) => {
        const { body, query = {} } = req;
        const { proxyAthlete, reqSource } = query;
        const { type } = body;
        body.to = moment(body.to).format("YYYY-MM-DD") + "T00:00:00.000Z";
        assert(
          !moment.utc(body.from).isSame(moment.utc(body.to), "day"),
          400,
          `You cannot ${type} a workout to the same day`
        );
        await api(
          `copy-workout?proxyAthlete=${proxyAthlete}&reqSource=${reqSource}`,
          { method: "POST", body: omit(body, "type") }
        );
        flash("success", `Successfully ${type}`);

        res.refreshWith(
          { modal: "", day: moment.utc(body.to).format(DATE_DAY_FORMAT) },
          { url: "back" }
        );
      }
    )
    .post(
      "/exercise/paste-exercises",
      async ({ req, res, flash, api, locals }) => {
        const { body } = req;
        const {
          activeUserToRequest: { primaryExercises = {}, _id: ownerId } = {},
        } = locals;
        const exerciseInLocalStorage = localStorage.getItem("exercisesToCopy");
        const exercises = JSON.parse(exerciseInLocalStorage);

        const endpoint = body.isProgram
          ? `paste-exercises-program`
          : `paste-exercises-calendar`;

        const { isProgram, ...restBody } = body; //deleting isProgram from the body
        const updatedBody = {
          ...restBody,
          userInSessionPrimaryExercises: primaryExercises,
          exercises,
          ownerId,
        };
        await api(endpoint, {
          method: "POST",
          body: updatedBody,
        });
        flash("success", `Successfully pasted`);

        res.refreshWith({ modal: "" }, { url: "back" });
      }
    )

    .post("/exercise/copy-exercises", async ({ req, res, flash }) => {
      const { body } = req;
      const { exercises, originalAthleteLibrary = [] } = body;
      const now = new Date();
      const timestamp = now.getTime();
      const queryToRefresh = `${exercises[0]?._id}${timestamp}`;
      localStorage.setItem("exercisesToCopy", JSON.stringify(exercises));
      localStorage.setItem(
        "originalAthleteLibraryToCopy",
        JSON.stringify(originalAthleteLibrary)
      );
      flash("success", `Successfully copied`);
      res.refreshWith(
        { exercisesInClipboard: queryToRefresh },
        { url: "back" }
      );
    })
    // Moves one day of exercises to another.
    .post(
      "/exercise/move-day",
      async ({ req, res, assert, flash, api, session }) => {
        const { body, query = {} } = req;
        const { proxyAthlete, reqSource, dnd } = query;
        const { type } = body;
        assert(body.to, 400, "please select a date");
        body.to = moment(body.to).format("YYYY-MM-DD") + "T00:00:00.000Z";
        assert(
          !moment.utc(body.from).isSame(moment(body.to), "day"),
          400,
          `You cannot ${type} a workout to the same day`
        );
        await api(
          `move-workout?proxyAthlete=${proxyAthlete}&reqSource=${reqSource}`,
          { method: "POST", body: omit(body, "type") }
        );
        flash("success", `Successfully ${type}d workout`);

        if (dnd) return res.backDontScroll();

        res.refreshWith(
          { modal: "", day: moment.utc(body.to).format(DATE_DAY_FORMAT) },
          { url: "back" }
        );
      }
    )
    .post(
      "/exercise/shift-day",
      async ({ req, res, assert, flash, api, session }) => {
        const { body, query = {} } = req;
        const { from } = body;
        const { proxyAthlete } = query;

        assert(from, 400, "Please select a date");

        await api(`shift-program?proxyAthlete=${proxyAthlete}`, {
          method: "POST",
          body,
        });
        flash("success", `Successfully shifted`);

        res.refreshWith(
          { modal: "", day: moment.utc(from).format(DATE_DAY_FORMAT) },
          { url: "back" }
        );
      }
    )
    .post(
      "/exercise/order",
      async ({ req, res, api, assert, session, flash, locals }) => {
        const body = req.body;
        const athlete = get(req, "query.proxyAthlete");

        await api(`cycle-exercise-order?proxyAthlete=${athlete}`, {
          method: "POST",
          body,
        });
        res.refreshWith({}, { url: "back" });

        flash("success", "Successfully saved");
      }
    )
    .post("/exercise/reorder", async ({ req, res, api, flash, session }) => {
      const response = await api(
        `cycle-exercise-reorder?proxyAthlete=${get(req, "query.proxyAthlete")}`,
        { method: "POST", body: req.body }
      );
      const data = await response.json();
      setSession(session, "editCycle", data.cycle);
      setSession(session, "editExercises", data.programExercises);
      res.refreshWith({}, { url: "back" });

      flash("success", "Successfully saved");
    })
    .post(
      "/exercise/single-workout-order",
      async ({ req, res, api, flash, session }) => {
        const { body, query = {} } = req;
        const { proxyAthlete, reqSource } = query;
        await api(
          `order-exercises?proxyAthlete=${proxyAthlete}&reqSource=${reqSource}`,
          { method: "POST", body: body }
        );
        res.refreshWith({}, { url: "back" });

        flash("success", "Successfully saved");
      }
    )
    .post("/exercise/workout-reorder", async ({ req, res, api, flash }) => {
      const { body } = req;
      if (body.toDate) {
        body.toDate =
          moment(body.toDate).format("YYYY-MM-DD") + "T00:00:00.000Z";
      }

      await api("reorder-exercises", { method: "POST", body: body });
      res.backDontScroll();

      flash("success", "Successfully saved");
    })
    // Update rep max for exercise.
    .post(
      "/exercise/rep-max/:exercise?",
      async ({ req, res, api, load, locals, assert }) => {
        const { params, body } = req;
        await load("editExercise", { exercise: params.exercise });
        const { editExercise, activeUser } = locals;
        assert(body.primary, 400, "You must specify a primary exercise.");
        assert(body.repMax, 400, "You must specify a rep maximum.");
        set(editExercise, `repMaximums.${body.primary}`, body.repMax);

        if (body.overwrite) {
          set(activeUser, `repMaximums.${body.primary}`, body.repMax);
          await api(`user/${activeUser._id}`, {
            method: "PUT",
            body: { repMaximums: get(activeUser, "repMaximums", null) },
          });
        }

        res.refreshWith({ modal: "" }, { url: "back" });
      }
    )
    // Create or update a set at a specific index in an exercise.
    .post(
      "/exercise/sets/save/:index/:exercise?",
      async ({ req, res, assert, flash, locals, api, load }) => {
        const { params, body } = req;
        await load("editExercise", { exercise: params.exercise });
        const { editExercise, activeUser } = locals;
        const index = get(params, "index");
        const primary = get(editExercise, "primary");
        const secondary = get(editExercise, "secondary", "");
        const repMaxPath = `repMaximums.${primary}`;
        const repMax = body.equipped
          ? body.equippedRepMax
          : get(editExercise, repMaxPath, get(activeUser, repMaxPath, 0));

        const isAssistant = get(editExercise, "type") === "assistant";

        assert(
          isAssistant || repMax,
          400,
          "Rep Maximum must be greater than 0 to add a set."
        );

        if (body.equipped) {
          assert(
            body.equipment,
            400,
            "You must specify the equipment for an equipped set."
          );
          assert(
            body.equippedRepMax > 0,
            400,
            "You must specify an equipped rep maximum for this set"
          );
        } else {
          delete body.equipment;
          delete body.equippedRepMax;
        }

        if (body.loadType === "%")
          body.load = repMax * (Number(body.percentRepMax) / 100);
        delete body.loadType;
        body.percentRepMax;

        assert(body.sets > 0, 400, "Sets must be greater than 0");

        if (body.rpe !== "AMRAP") {
          assert(body.reps > 0, 400, "Reps must be greater than 0");
        }

        if (process.browser && !isAssistant) {
          if (
            body.load > repMax &&
            !confirm("Are you sure you want to program above 100% of 1RM?")
          ) {
            return res.backDontScroll();
          }
        }

        editExercise.sets.splice(index, 1, body);

        flash("success", "Successfully updated");
        const modalToDisplay = get(body, "addAnother", false)
          ? { type: "edit-set" }
          : "";

        res.refreshWith({ modal: modalToDisplay }, { url: "back" });
      }
    )
    // Move a set within an exercise.
    .get(
      "/exercise/sets/move/:index/:to/:exercise?",
      async ({ req, res, locals, api, load, assert }) => {
        const { params } = req;
        await load("editExercise", { exercise: params.exercise });
        const {
          editExercise: { sets },
        } = locals;
        const [temp] = sets.splice(params.index, 1);
        sets.splice(params.to, 0, temp);
        res.backDontScroll();
      }
    )
    // Remove a set from an exercise.
    .get(
      "/exercise/sets/remove/:index/:exercise?",
      async ({ req, res, locals, load }, next) => {
        if (
          process.browser &&
          !confirm("Are you sure you want to delete this set?")
        ) {
          return res.backDontScroll();
        }

        const { query, params } = req;
        await load("editExercise", { exercise: params.exercise });
        const {
          editExercise: { sets },
        } = locals;
        sets.splice(params.index, 1);
        res.backDontScroll();
      }
    )
    // Remove a set from an exercise in the quick-edit.
    .get(
      "/exercise/quick-edit/sets/remove/:index/:exercise/:organizationCoach/:proxyAthlete?",
      async ({ req, res, flash, locals, load, api }, next) => {
        const { params, query = {} } = req;
        const {
          exercise,
          index,
          organizationCoach = undefined,
          proxyAthlete,
        } = params;
        const { reqSource } = query;

        await load("editExercise", {
          exercise: exercise,
          proxyAthlete: proxyAthlete,
          organizationCoach: organizationCoach,
        });

        await quickEditRemoveSetRequests(
          locals,
          api,
          proxyAthlete,
          reqSource,
          flash,
          index,
          organizationCoach
        );

        res.refreshWith(
          {
            edit: true,
            editExerciseId: locals.editExercise._id,
            weekIndex: locals.editExercise.cycle.programWeek,
            sessionIndex: locals.editExercise.cycle.programSession - 1,
          },
          { url: "back" }
        );
      }
    )
    // Cancel the quick-edit.
    .get("/exercise/quick-edit/cancel", async ({ res }) => {
      res.refreshWith({ edit: "" }, { url: "back" });
    })
    // add a new set in the quick-edit.
    .post(
      "/exercise/quick-edit/sets/add/:index/:exercise/:proxyAthlete",
      async ({ req, res, assert, flash, locals, load, api }, next) => {
        const { body, params, query = {} } = req;
        const { newSet, defaultRepMax, organizationCoach = undefined } = body;
        const { exercise, index, proxyAthlete } = params;
        const { reqSource } = query;

        await load("editExercise", {
          exercise: exercise,
          proxyAthlete: proxyAthlete,
          organizationCoach: organizationCoach,
        });
        const {
          editExercise,
          editExercise: { sets },
        } = locals;

        const isAssistant =
          get(editExercise, "type", body.type) === "assistant";

        // Validations
        if (!editExercise.parameters) {
          validationsForNewSet(newSet, defaultRepMax, isAssistant, assert);
        }

        let queryRequest = `/?proxyAthlete=${proxyAthlete}&reqSource=${reqSource}`;

        if (organizationCoach) {
          queryRequest =
            queryRequest + `&organizationCoach=${body.organizationCoach}`;
        }

        delete body.organizationCoach;

        try {
          console.log("queryRequest", queryRequest);
          // Adding the new set in index+1 of the array
          sets.splice(parseInt(index) + 1, 0, newSet);
          await api(`exercise/${editExercise._id}` + queryRequest, {
            method: "PUT",
            body: editExercise,
          });

          flash("success", "Successfully added");
        } catch (err) {
          console.log("error:", err);
        }

        res.refreshWith(
          {
            edit: true,
            editExerciseId: editExercise._id,
            weekIndex: editExercise.cycle && editExercise.cycle.programWeek,
            sessionIndex:
              editExercise.cycle && editExercise.cycle.programSession - 1,
            setIndex: index,
          },
          { url: "back" }
        );
      }
    )
    // Duplicates exercise in the Calendar single day view
    .post(
      "/exercise/duplicate-from-single-day",
      async ({ req, res, assert, flash, locals, api }) => {
        const { body, query = {} } = req;
        const { proxyAthlete } = query;
        const _id = get(body, "_id");

        if (body.sets) {
          body.sets.forEach((set) => {
            if (
              set.video ||
              set.videoArray ||
              set.statusArray ||
              set.outcomes ||
              set.comments ||
              set.videos
            ) {
              delete set.video;
              delete set.videoArray;
              delete set.statusArray;
              delete set.outcomes;
              delete set.comments;
              delete set.videos;
            }
          });
        }

        delete body.nextSuperset;
        const cycle = await api(
          `exercise?proxyAthlete=${proxyAthlete}&reqSource=${"calendar"}`,
          {
            method: "POST",
            body,
          }
        );
        res.backDontScroll();
      }
    )
    // Duplicates exercise in the Program Builder
    .post(
      "/exercise/duplicate",
      async ({ req, res, assert, flash, locals, api }) => {
        const { body, query } = req;
        const proxyAthlete = query.proxyAthlete;

        // some how in some random cases a key/val pair is getting sent with undefined:undefined. This is just to clean up the body
        Object.keys(body).forEach(
          (key) => key === "undefined" && delete body[key]
        );

        const cycle = await api(
          `cycle-duplicate-exercise?proxyAthlete=${proxyAthlete}`,
          {
            method: "POST",
            body,
          }
        );
        res.backDontScroll();
      }
    )
    // Duplicates exercise for another workout in the Program Builder
    .post(
      "/exercise/duplicate-send-to-other-workout",
      async ({ req, res, assert, flash, locals, api }) => {
        const { body, query } = req;
        const proxyAthlete = query.proxyAthlete;

        assert(
          body.destination && body.destination.length,
          400,
          "Select at least one place where you want to duplicate to."
        );

        // some how in some random cases a key/val pair is getting sent with undefined:undefined. This is just to clean up the body
        Object.keys(body).forEach(
          (key) => key === "undefined" && delete body[key]
        );

        const cycle = await api(
          `cycle-duplicate-exercise-to-other-workout?proxyAthlete=${proxyAthlete}`,
          {
            method: "POST",
            body,
          }
        );
        res.refreshWith({ modal: "" }, { url: "back", hash: "#_" });
      }
    )
    // Remove a set from an exercise in the quick-edit.
    .post(
      "/exercise/quick-edit/sets/remove/:index/:exercise/:organizationCoach/:proxyAthlete?",
      async ({ req, res, flash, api }) => {
        const { params, query = {}, body } = req;
        const { index, organizationCoach = undefined, proxyAthlete } = params;
        const { reqSource } = query;

        await quickEditRemoveSetRequests(
          body,
          api,
          proxyAthlete,
          reqSource,
          flash,
          index,
          organizationCoach
        );

        res.refreshWith(
          {
            edit: true,
            editExerciseId: body.editExercise._id,
            weekIndex: body.editExercise.cycle.programWeek,
            sessionIndex: body.editExercise.cycle.programSession - 1,
          },
          { url: "back" }
        );
      }
    )
    // Update progression to exercise in the Program Builder
    .post(
      "/exercise/update-progression-to-exercise",
      async ({ req, res, api, flash, session }) => {
        const { body } = req;

        // some how in some random cases a key/val pair is getting sent with undefined:undefined. This is just to clean up the body
        Object.keys(body).forEach(
          (key) => key === "undefined" && delete body[key]
        );

        const response = await api(`cycle-progression-exercise`, {
          method: "PUT",
          body,
        });
        const data = await response.json();
        setSession(session, "editCycle", data.cycle);
        setSession(session, "editExercises", data.programExercises);

        flash("success", "Successfully updated");
        res.refreshWith({ modal: "" }, { url: "back", hash: "#_" });
      }
    )
    // Update progression to session in the Program Builder
    .post(
      "/exercise/update-progression-to-session",
      async ({ req, res, api, flash, session }) => {
        const { body } = req;

        Object.keys(body).forEach(
          (key) => key === "undefined" && delete body[key]
        );

        const response = await api(`cycle-progression-session`, {
          method: "PUT",
          body,
        });
        const data = await response.json();
        setSession(session, "editCycle", data.cycle);
        setSession(session, "editExercises", data.programExercises);

        flash("success", "Successfully updated");
        res.refreshWith({ modal: "" }, { url: "back", hash: "#_" });
      }
    )
    // Update progression to week in the Program Builder
    .post(
      "/exercise/update-progression-to-week",
      async ({ req, res, api, flash, session }) => {
        const { body, query = {} } = req;

        Object.keys(body).forEach(
          (key) => key === "undefined" && delete body[key]
        );

        const calendarMode = get(query, "calendarMode", false);

        const response = await api(
          calendarMode ? "calendar-progression" : "cycle-progression-week",
          {
            method: "PUT",
            body,
          }
        );
        const data = await response.json();
        setSession(session, "editCycle", data.cycle);
        setSession(session, "editExercises", data.programExercises);

        flash("success", "Successfully updated");
        res.refreshWith({ modal: "" }, { url: "back", hash: "#_" });
      }
    )
    .post("/exercise/superset/add", async ({ req, res, api, flash }) => {
      const {
        exercise,
        nextExercise,
        fullExercise,
        monthCalendar = false,
      } = req.body;
      await api(
        `exercise-set-next-superset?exercise=${exercise}&nextExercise=${nextExercise}`,
        {
          method: "PUT",
          body: {},
        }
      );

      flash("success", "Successfully added");

      if (monthCalendar) {
        return res.refreshWith({}, { url: "back" });
      }
      res.refreshWith(
        Object.assign(
          {},
          {
            edit: true,
            editExerciseId: fullExercise._id,
          },
          fullExercise.cycle &&
            fullExercise.cycle.programWeek && {
              weekIndex: fullExercise.cycle.programWeek,
            },
          fullExercise.cycle &&
            fullExercise.cycle.programSession && {
              sessionIndex: fullExercise.cycle.programSession - 1,
            }
        ),

        { url: "back" }
      );
    })
    .post("/exercise/superset/remove", async ({ req, res, api, flash }) => {
      const { exercise, fullExercise } = req.body;
      await api(`exercise-remove-next-superset?exercise=${exercise}`, {
        method: "PUT",
        body: {},
      });
      flash("success", "Successfully removed");
      res.refreshWith(
        Object.assign(
          {},
          {
            edit: true,
            editExerciseId: fullExercise._id,
          },
          fullExercise.cycle &&
            fullExercise.cycle.programWeek && {
              weekIndex: fullExercise.cycle.programWeek,
            },
          fullExercise.cycle &&
            fullExercise.cycle.programSession && {
              sessionIndex: fullExercise.cycle.programSession - 1,
            }
        ),

        { url: "back" }
      );
    })
    .post(
      "/exercise/edit-video-record",
      async ({ req, res, flash, api, locals, assert }) => {
        const { date, shareLink, athleteId, _id, type, reportDate } = req.body;
        const { height, width, sharedUrl, embedUrl } = shareLink;

        assert(sharedUrl, 400, "The video URL should not be empty");

        assert(validURL(sharedUrl), 400, "The video URL is not valid");

        await api(`video-record${_id ? `/${_id}` : ""}`, {
          method: _id ? "PUT" : "POST",
          body: {
            date,
            width,
            height,
            sharedUrl,
            userId: athleteId,
            coachId: locals.activeUser._id,
            embedUrl,
            avatar:
              !!locals.activeUser.avatar && !!locals.activeUser.avatar.file
                ? locals.activeUser.avatar.file
                : null,
            firebaseProjectId: !!locals.activeUserToRequest.firebaseProjectId
              ? locals.activeUserToRequest.firebaseProjectId
              : defaultFirebaseProjectId,
            type,
            reportDate,
          },
        });
        flash("success", "Successfully saved");

        res.backDontScroll();
      }
    )
    .get(
      "/exercise/delete-video-record/:_id",
      async ({ req, res, flash, api }) => {
        const { _id } = req.params;

        await api(`video-record/${_id}`, {
          method: "DELETE",
        });
        flash("success", "Successfully deleted");

        res.backDontScroll();
      }
    )
    .post(
      "/exercise/swap",
      async ({ api, assert, flash, load, req, res, locals }) => {
        const { body } = req;
        const {
          typeFrom,
          primaryFrom,
          typeTo,
          primaryTo,
          movementTo,
          secondaryFrom,
          proxyAthlete,
        } = body;

        assert(typeFrom, 400, "Type to swap from is required");
        assert(primaryFrom, 400, "Exercise to swap from is required");
        assert(typeTo, 400, "Type to swap to is required");
        assert(primaryTo, 400, "Exercise to swap to is required");
        assert(movementTo, 400, "Name to swap to is required");
        if (typeFrom === "secondary") {
          assert(secondaryFrom, 400, "Exercise to swap from is required");
        }

        // add secondary movement to athlete's library
        if (proxyAthlete && typeTo !== "primary") {
          await load("profileUser", { user: proxyAthlete });
          const { activeUserToRequest, profileUser } = locals;
          const coachLibrary =
            activeUserToRequest.exerciseLibrary[primaryTo] || [];
          const libraryMovement = coachLibrary.find(
            ({ movement }) => movement === movementTo
          );
          if (libraryMovement) {
            const movement = Object.assign({}, libraryMovement, {
              // use the athlete's RM
              repMaximum: profileUser.repMaximums[primaryTo],
              primary: primaryTo,
            });
            await copySecondaryMovement(movement, profileUser, api);
          }
        }

        await api("swap-exercise", {
          method: "POST",
          body,
        });
        flash("success", "Successfully swapped");

        res.refreshWith(
          { modal: "", editMode: "", exercise: "" },
          { url: "back", hash: "#_" }
        );
      }
    )
    // Create or update an exercise
    .post(
      "/exercise/edit-set-video",
      async ({ req, res, flash, locals, api, load }) => {
        const { body, params, query = {} } = req;

        await load("editExercise", {
          exercise: params.exercise,
          proxyAthlete: query.proxyAthlete,
        });

        const { editExercise } = locals;
        const _id = get(editExercise, "_id");

        await api(`review-video-exercise`, { method: "PUT", body: body });

        res.refreshWith(
          { editMode: "", movement: "", scrollTo: _id },
          { url: "back", hash: "#_" }
        );
      }
    );
};

export default Exercise;
